import {
  Document, Page, Text, View, StyleSheet, Font,
} from "@react-pdf/renderer";
import { resolveBaseWeight, calcTotalLoss, calcTotalLossPct, calcTherapyWeek, selectWeighedEntries, formatWeightChange, formatWeightChangePct, weightChangeLabel } from "../utils/therapyStats";
import { isFutureEntry } from "../utils/entryDate";

const fontBase = typeof window !== "undefined"
  ? window.location.origin
  : "";

Font.register({
  family: "Roboto",
  fonts: [
    { src: `${fontBase}/fonts/Roboto-Regular.woff`, fontWeight: "normal" },
    { src: `${fontBase}/fonts/Roboto-Bold.woff`, fontWeight: "bold" },
  ],
});

const GREEN = "#1a5c46";
const GREEN_LIGHT = "#e8f5f0";
const GRAY = "#6b7280";
const DARK = "#111827";
const BORDER = "#e5e7eb";

const s = StyleSheet.create({
  page: { fontFamily: "Roboto", fontSize: 9, color: DARK, padding: 36 },
  // header
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  logoBox: { flexDirection: "row", alignItems: "center", gap: 6 },
  logoBadge: { width: 28, height: 28, backgroundColor: GREEN, borderRadius: 6, justifyContent: "center", alignItems: "center" },
  logoBadgeText: { color: "white", fontSize: 11, fontFamily: "Roboto", fontWeight: "bold" },
  logoText: { fontSize: 16, fontFamily: "Roboto", fontWeight: "bold", color: DARK },
  headerRight: { alignItems: "flex-end" },
  headerEmail: { fontSize: 8, color: GRAY },
  headerDate: { fontSize: 8, color: GRAY, marginTop: 2 },
  // section
  sectionTitle: { fontSize: 10, fontFamily: "Roboto", fontWeight: "bold", color: GREEN, marginBottom: 6, marginTop: 16 },
  // summary cards
  cards: { flexDirection: "row", gap: 8, marginBottom: 4 },
  card: { flex: 1, backgroundColor: GREEN_LIGHT, borderRadius: 6, padding: 8 },
  cardLabel: { fontSize: 7, color: GREEN, marginBottom: 2 },
  cardValue: { fontSize: 13, fontFamily: "Roboto", fontWeight: "bold", color: GREEN },
  cardSub: { fontSize: 7, color: GREEN, marginTop: 1 },
  // table
  table: { borderRadius: 6, overflow: "hidden", border: `1pt solid ${BORDER}` },
  tableHeader: { flexDirection: "row", backgroundColor: GREEN, paddingVertical: 5, paddingHorizontal: 8 },
  tableHeaderCell: { fontFamily: "Roboto", fontWeight: "bold", fontSize: 8, color: "white" },
  tableRow: { flexDirection: "row", paddingVertical: 4, paddingHorizontal: 8, borderBottomWidth: 1, borderBottomColor: BORDER },
  tableRowAlt: { backgroundColor: "#f9fafb" },
  tableCell: { fontSize: 8, color: DARK },
  // col widths weight table
  wCol1: { width: "30%" },
  wCol2: { width: "30%" },
  wCol3: { width: "40%" },
  // col widths dose table
  dCol1: { width: "40%" },
  dCol2: { width: "60%" },
  // col widths side effects
  seCol1: { width: "40%" },
  seCol2: { width: "30%" },
  seCol3: { width: "30%" },
  // doctor summary
  summaryBox: { backgroundColor: GREEN_LIGHT, borderRadius: 8, padding: 12, marginTop: 4 },
  summaryText: { fontSize: 8.5, lineHeight: 1.6, color: DARK },
  // disclaimer
  disclaimer: { marginTop: 24, borderTopWidth: 1, borderTopColor: BORDER, paddingTop: 10 },
  disclaimerText: { fontSize: 7.5, color: GRAY, textAlign: "center", lineHeight: 1.5 },
  divider: { height: 1, backgroundColor: BORDER, marginTop: 16 },
});

interface Entry {
  // A D2 döntés óta hiányozhat (dózis-only bejegyzés) — `null` a drótról.
  weight?: number | null;
  dose?: number;
  date?: string;
  mellekhat?: Record<string, number>;
  createdAt?: { seconds: number };
  glucose?: number | null;
}

interface Profil {
  gyogyszer?: string;
  kezdoSuly?: string;
  celSuly?: string;
  terapiaKezdete?: string;
}

interface Props {
  entries: Entry[];
  profil: Profil;
  userEmail: string;
}

const MELLEKHAT_LABELS: Record<string, string> = {
  hanyinger: "Hányinger",
  szekrekedes: "Székrekedés",
  "hasmenés": "Hasmenés",
  "fáradtság": "Fáradtság",
  reflux: "Reflux",
  "fejfájás": "Fejfájás",
};

const fmtDate = (d: string | undefined, seconds?: number) => {
  if (d) return new Date(d).toLocaleDateString("hu-HU");
  if (seconds) return new Date(seconds * 1000).toLocaleDateString("hu-HU");
  return "–";
};

export default function GlpRiportPdf({ entries, profil, userEmail }: Props) {
  const sorted = [...entries].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : (a.createdAt?.seconds ?? 0) * 1000;
    const db2 = b.date ? new Date(b.date).getTime() : (b.createdAt?.seconds ?? 0) * 1000;
    return da - db2;
  });

  // Jövőbeli dátumú bejegyzés (elgépelt évszám/nap) kizárva az összegző
  // mezőkből (kezdő/jelenlegi súly, dózis, mellékhatás-átlag) — a táblázatok
  // (Súlyváltozás napló, Dózis előzmények, Éhomi vércukor) továbbra is a
  // teljes, szűretlen naplót mutatják, mint a Napló-lista az appban
  // (2026-08-17 QA-lelet).
  const computableSorted = sorted.filter(e => !isFutureEntry(e));

  // Csak a MÉRT bejegyzések: a súly nélküli (dózis-only) sorok nem
  // vihetik el a bázist és a záró súlyt — a PDF különben "null kg"-ot és
  // ebből számolt hamis változást írt volna (2026-09-16 lelet).
  const weighedSorted = selectWeighedEntries(computableSorted);
  const first = weighedSorted[0];
  const last = weighedSorted[weighedSorted.length - 1];
  // A PDF-riport nem ismer időszak-fogalmat (mindig a teljes napló-history
  // kerül bele), ezért isFullHistory=true: a profil kezdő súlya a kanonikus
  // bázis, fallback az első bejegyzésre — ugyanaz a forrás, mint a Főoldal
  // "Összes fogyás" kártyája (docs/FOGALMAK.md).
  const firstWeight = resolveBaseWeight(profil.kezdoSuly, first?.weight, true);
  const lastWeight = last?.weight;
  const totalLoss = calcTotalLoss(firstWeight, lastWeight);
  const totalLossPct = calcTotalLossPct(firstWeight, totalLoss);
  const therapyWeeks = profil.terapiaKezdete
    ? calcTherapyWeek(new Date(profil.terapiaKezdete))
    : null;
  const doseEntries = sorted.filter(e => e.dose && e.dose > 0);
  const computableDoseEntries = computableSorted.filter(e => e.dose && e.dose > 0);
  const lastDose = computableDoseEntries[computableDoseEntries.length - 1]?.dose;
  const glucoseEntries = sorted.filter(e => e.glucose != null);

  // Side effects aggregation — a computableSorted-ból, jövőbeli bejegyzés
  // mellékhatását nem számítjuk bele az átlagba (még nem történt meg).
  const mhAgg: Record<string, { total: number; count: number }> = {};
  computableSorted.forEach(e => {
    if (!e.mellekhat) return;
    Object.entries(e.mellekhat).forEach(([k, v]) => {
      if (v > 0) {
        if (!mhAgg[k]) mhAgg[k] = { total: 0, count: 0 };
        mhAgg[k].total += v;
        mhAgg[k].count += 1;
      }
    });
  });
  const mhRows = Object.entries(mhAgg).map(([k, v]) => ({
    label: MELLEKHAT_LABELS[k] || k,
    avg: (v.total / v.count).toFixed(1),
    count: v.count,
  }));

  // Doctor summary text
  // 2026-08-17: nincs default gyógyszer-név — az onboarding mostantól
  // üresen hagyhatja a mezőt, egy csendben "Tirzepatide"-ra eső riport
  // orvosnak mutatott téves adat lenne (lásd docs/FOGALMAK.md).
  const gyogyszerNev = profil.gyogyszer === "semaglutide" ? "Semaglutide"
    : profil.gyogyszer === "tirzepatide" ? "Tirzepatide"
    : null;
  const docSummary = [
    `Páciens: ${userEmail}`,
    gyogyszerNev ? `Gyógyszer: ${gyogyszerNev}` : null,
    profil.terapiaKezdete ? `Terápia kezdete: ${new Date(profil.terapiaKezdete).toLocaleDateString("hu-HU")}${therapyWeeks ? ` (${therapyWeeks}. hét)` : ""}` : null,
    firstWeight ? `Kezdő testsúly: ${firstWeight} kg` : null,
    lastWeight ? `Jelenlegi testsúly: ${lastWeight} kg` : null,
    totalLoss !== null && totalLoss > 0 ? `Testsúlycsökkenés: ${totalLoss.toFixed(1)} kg (${totalLossPct?.toFixed(1)}%)` : null,
    profil.celSuly ? `Célsúly: ${profil.celSuly} kg` : null,
    lastDose ? `Jelenlegi dózis: ${lastDose} mg` : null,
    sorted.length > 0 ? `Rögzített bejegyzések száma: ${sorted.length}` : null,
    mhRows.length > 0
      ? `Leggyakoribb mellékhatások: ${mhRows.map(r => `${r.label} (átl. ${r.avg}/5, ${r.count}×)`).join(", ")}`
      : "Jelentős mellékhatás nem került rögzítésre.",
  ].filter(Boolean).join("\n");

  const today = new Date().toLocaleDateString("hu-HU");

  // Weight rows with delta
  // A súly-tábla is csak a mért sorokat hozza, és a delta a MEGELŐZŐ MÉRT
  // súlyhoz képest számol — nem a lista előző eleméhez, ami lehet
  // dózis-only.
  const weighedRowSource = selectWeighedEntries(sorted);
  const weightRows = weighedRowSource.map((e, i) => {
    const prev = i > 0 ? weighedRowSource[i - 1].weight : null;
    const delta = prev != null && e.weight != null ? (e.weight - prev) : null;
    return {
      date: fmtDate(e.date, e.createdAt?.seconds),
      weight: `${e.weight} kg`,
      delta: delta == null ? "–" : delta <= 0 ? `${delta.toFixed(1)} kg` : `+${delta.toFixed(1)} kg`,
    };
  });

  return (
    <Document title="GLP Napló – Terápia riport" author="GLP Napló">
      <Page size="A4" style={s.page}>

        {/* Header */}
        <View style={s.header}>
          <View style={s.logoBox}>
            <View style={s.logoBadge}>
              <Text style={s.logoBadgeText}>G</Text>
            </View>
            <Text style={s.logoText}>GLP Napló</Text>
          </View>
          <View style={s.headerRight}>
            <Text style={s.headerEmail}>{userEmail}</Text>
            <Text style={s.headerDate}>Generálva: {today}</Text>
          </View>
        </View>

        <View style={s.divider} />

        {/* Summary cards */}
        <Text style={s.sectionTitle}>Összefoglaló</Text>
        <View style={s.cards}>
          <View style={s.card}>
            <Text style={s.cardLabel}>Kezdő súly</Text>
            <Text style={s.cardValue}>{firstWeight ?? "–"} kg</Text>
          </View>
          <View style={s.card}>
            <Text style={s.cardLabel}>Jelenlegi súly</Text>
            <Text style={s.cardValue}>{lastWeight ?? "–"} kg</Text>
          </View>
          <View style={s.card}>
            <Text style={s.cardLabel}>{weightChangeLabel(totalLoss, "total")}</Text>
            <Text style={s.cardValue}>{formatWeightChange(totalLoss).text}</Text>
            {formatWeightChangePct(totalLossPct) && <Text style={s.cardSub}>{formatWeightChangePct(totalLossPct)}</Text>}
          </View>
          <View style={s.card}>
            <Text style={s.cardLabel}>Kezelés</Text>
            <Text style={s.cardValue}>{therapyWeeks ?? "–"}</Text>
            {therapyWeeks && <Text style={s.cardSub}>hét</Text>}
          </View>
          <View style={s.card}>
            <Text style={s.cardLabel}>Dózis</Text>
            <Text style={s.cardValue}>{lastDose ?? "–"}{lastDose ? " mg" : ""}</Text>
            <Text style={s.cardSub}>{gyogyszerNev ?? "–"}</Text>
          </View>
        </View>

        {/* Weight table */}
        <Text style={s.sectionTitle}>Súlyváltozás napló</Text>
        <View style={s.table}>
          <View style={s.tableHeader}>
            <Text style={[s.tableHeaderCell, s.wCol1]}>Dátum</Text>
            <Text style={[s.tableHeaderCell, s.wCol2]}>Súly</Text>
            <Text style={[s.tableHeaderCell, s.wCol3]}>Változás</Text>
          </View>
          {weightRows.map((r, i) => (
            <View key={i} style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}>
              <Text style={[s.tableCell, s.wCol1]}>{r.date}</Text>
              <Text style={[s.tableCell, s.wCol2]}>{r.weight}</Text>
              {/* A5 (külső audit, 2026-10-14): a változás SEMLEGES színű mindkét
                  irányban — az előjel mondja meg az irányt. Korábban a növekedés
                  piros, a fogyás zöld volt: riasztás- és minősítés-szemantika egy
                  orvosnak szánt dokumentumban (2. szabály). */}
              <Text style={[s.tableCell, s.wCol3]}>
                {r.delta}
              </Text>
            </View>
          ))}
        </View>

        {/* Dose table */}
        {doseEntries.length > 0 && (
          <>
            <Text style={s.sectionTitle}>Dózis előzmények</Text>
            <View style={s.table}>
              <View style={s.tableHeader}>
                <Text style={[s.tableHeaderCell, s.dCol1]}>Dátum</Text>
                <Text style={[s.tableHeaderCell, s.dCol2]}>Dózis</Text>
              </View>
              {doseEntries.map((e, i) => (
                <View key={i} style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}>
                  <Text style={[s.tableCell, s.dCol1]}>{fmtDate(e.date, e.createdAt?.seconds)}</Text>
                  <Text style={[s.tableCell, s.dCol2]}>{e.dose} mg</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* Glucose table */}
        {glucoseEntries.length > 0 && (
          <>
            <Text style={s.sectionTitle}>Éhomi vércukor</Text>
            <View style={s.table}>
              <View style={s.tableHeader}>
                <Text style={[s.tableHeaderCell, s.dCol1]}>Dátum</Text>
                <Text style={[s.tableHeaderCell, s.dCol2]}>Érték</Text>
              </View>
              {glucoseEntries.map((e, i) => (
                <View key={i} style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}>
                  <Text style={[s.tableCell, s.dCol1]}>{fmtDate(e.date, e.createdAt?.seconds)}</Text>
                  <Text style={[s.tableCell, s.dCol2]}>{e.glucose} mmol/l</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* Side effects */}
        {mhRows.length > 0 && (
          <>
            <Text style={s.sectionTitle}>Mellékhatások összesítése</Text>
            <View style={s.table}>
              <View style={s.tableHeader}>
                <Text style={[s.tableHeaderCell, s.seCol1]}>Mellékhatás</Text>
                <Text style={[s.tableHeaderCell, s.seCol2]}>Átl. súlyosság</Text>
                <Text style={[s.tableHeaderCell, s.seCol3]}>Előfordulás</Text>
              </View>
              {mhRows.map((r, i) => (
                <View key={i} style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}>
                  <Text style={[s.tableCell, s.seCol1]}>{r.label}</Text>
                  <Text style={[s.tableCell, s.seCol2]}>{r.avg} / 5</Text>
                  <Text style={[s.tableCell, s.seCol3]}>{r.count}×</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* Doctor summary */}
        <Text style={s.sectionTitle}>Orvosnak szóló összefoglaló</Text>
        <View style={s.summaryBox}>
          <Text style={s.summaryText}>{docSummary}</Text>
        </View>

        {/* Disclaimer */}
        <View style={s.disclaimer}>
          <Text style={s.disclaimerText}>
            Ez az összefoglaló nem helyettesíti az orvosi konzultációt.{"\n"}
            A GLP Napló alkalmazás által generált adat tájékoztató jellegű. Minden egészségügyi döntést kezelőorvosával együtt hozzon meg.{"\n"}
            glpnaplo.hu — {today}
          </Text>
        </View>

      </Page>
    </Document>
  );
}
